
import React, { useState, useEffect, useContext, useRef } from "react";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { handleSocialLogin } from "./socialMediaHandlers";
import MDTypography from "components/MDTypography";
import { v4 as uuidv4 } from "uuid";

import MDButton from "components/MDButton";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import UserService from "services/user-service"; // [NEW] workspace switching

// Auth imports
import { AuthContext } from "context";
import AuthService from "services/auth-service";
import { useNavigate } from "react-router-dom";

import facebookIcon from "assets/images/icons/socials/facebook.png";
import youtubeIcon from "assets/images/icons/socials/youtube.png";
import instagramIcon from "assets/images/icons/socials/instagram.png";



const socialPlatforms = [
  { name: "Facebook", icon: facebookIcon, handler: "facebook" },
  { name: "YouTube", icon: youtubeIcon, handler: "youtube" },
  { name: "Instagram", icon: instagramIcon, handler: "instagram" },
];

import ConnectOwnProfileStep from "layouts/tutorial/steps/ConnectOwnProfileStep";

import InstagramInstructions from "./InstagramInstructions";

function ConnectOwnProfile({ open, onClose, canClose = true, children }) {

  const [loading, setLoading] = useState(false);
  const [showInstagramInfo, setShowInstagramInfo] = useState(false); // [NEW] State for instructions
  const [showCard, setShowCard] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // [NEW] Kebab menu state
  const [kebabAnchor, setKebabAnchor] = useState(null);
  const handleKebabOpen = (e) => setKebabAnchor(e.currentTarget);
  const handleKebabClose = () => setKebabAnchor(null);

  // [NEW] Workspace switcher state (inline, like navbar)
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  const handleWorkspaceMenuOpen = async (e) => {
    setWorkspaceMenuAnchor(e.currentTarget);
    setLoadingWorkspaces(true);
    try {
      const response = await UserService.fetchUserWorkspaces();
      const list = Array.isArray(response) ? response : (response.data || []);
      setWorkspaces(list);
    } catch (err) {
      console.error("Failed to fetch workspaces", err);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);

  const handleSwitchWorkspace = async (workspaceId) => {
    try {
      await UserService.switchActiveWorkspace(workspaceId);
      localStorage.setItem("activeWorkspaceId", workspaceId);
      window.dispatchEvent(new CustomEvent("profile-updated"));
      handleWorkspaceMenuClose();
      handleKebabClose();
    } catch (err) {
      console.error("Failed to switch workspace", err);
    }
  };

  // [NEW] Progress State
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const wsRef = useRef(null); // [NEW] WebSocket ref

  // Tutorial State
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Check Tutorial Status on Mount
  useEffect(() => {
    const checkTutorial = async () => {
      try {
        const resp = await AuthService.getProfile();
        const userData = resp.data.attributes;
        setCurrentUser(userData);

        // [NEW] Demo User Bypass: Never show this specific tutorial step
        if (userData.role === "demo_viewer") {
          return;
        }

        const completedSteps = userData.completed_tutorials || [];
        // If strictly NOT completed, show tutorial
        if (!completedSteps.includes("connect_own_profile")) {
          setShowTutorial(true);
        }
      } catch (error) {
        console.error("Failed to check tutorial status:", error);
      }
    };

    // Only check if the modal is intended to be open
    if (open) {
      checkTutorial();
    }
  }, [open]);

  const handleCompleteTutorial = async () => {
    try {
      // Update backend
      const currentCompleted = currentUser?.completed_tutorials || [];
      if (!currentCompleted.includes("connect_own_profile")) {
        const updatedTutorials = [...currentCompleted, "connect_own_profile"];
        await AuthService.updateProfile({
          data: { attributes: { completed_tutorials: updatedTutorials } }
        });
      }
      setShowTutorial(false);
    } catch (error) {
      console.error("Failed to complete tutorial:", error);
      setShowTutorial(false);
    }
  };

  const handleSkipTutorial = () => {
    // Just close locally for now
    setShowTutorial(false);
  };


  useEffect(() => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: "v17.0",
      });
      window.FB.AppEvents.logPageView();
    };

    const loadFacebookSDK = () => {
      if (document.getElementById("facebook-jssdk")) return;
      const js = document.createElement("script");
      js.id = "facebook-jssdk";
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      document.body.appendChild(js);
    };
    loadFacebookSDK();
  }, []);

  useEffect(() => {
    if (open) {
      setShowCard(true);
    } else {
      const timeout = setTimeout(() => setShowCard(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleCloseClick = () => {
    if (!canClose) return; // prevent closing when not allowed

    setShowCard(false);
    setTimeout(onClose, 300);
  };

  const handleConnectClick = (platformHandler) => {
    if (platformHandler === "instagram") {
      setShowInstagramInfo(true);
    } else {
      handleConnect(platformHandler);
    }
  };

  const handleConnect = async (handlerName) => {
    setLoading(true);
    setErrorMessage("");
    setSelectedPlatform(handlerName);
    setShowInstagramInfo(false);
    try {
      const clientId = `client-${uuidv4()}`;

      const wsBaseUrl = process.env.REACT_APP_API_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${wsBaseUrl}/ws?clientId=${clientId}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.progress !== undefined) {
            setProgress(Math.floor(data.progress));
          }
          if (data.status) {
            setStatusMessage(data.status);
          }
        } catch (err) {
          console.warn("⚠️ Invalid WebSocket message:", event.data);
        }
      };

      ws.onerror = (err) => console.error("❌ WebSocket error:", err);

      await handleSocialLogin(handlerName, clientId);

      if (wsRef.current) wsRef.current.close();

      if (["facebook", "instagram"].includes(handlerName)) {
        handleCloseClick();
        window.location.reload();
      }
    } catch (error) {
      console.error("Error during social login:", error);
      setErrorMessage(error.message || "An error occurred while connecting.");
    } finally {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setLoading(false);
      setProgress(0);
      setStatusMessage("");
    }
  };

  const handleLogOut = async () => {
    await AuthService.logout();
    authContext.logout();
    navigate("/authentication/sign-in");
  };

  if (showTutorial) {
    return (
      <ConnectOwnProfileStep
        open={true}
        onComplete={handleCompleteTutorial}
        onSkip={handleSkipTutorial}
        user={currentUser}
      />
    );
  }

  return (
    <Backdrop
      open={open || showCard}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        transition: "opacity 0.3s ease-in-out",
      }}
    >
      <Slide direction="up" in={showCard} mountOnEnter unmountOnExit>
        <Card
          sx={{
            width: "90%",
            maxWidth: 600,
            p: 3,
            backgroundColor: "white",
            borderRadius: "16px",
            position: "relative",
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
          }}
        >
          {canClose && (
            <IconButton
              onClick={handleCloseClick}
              sx={{ position: "absolute", top: 5, right: 5 }}
            >
              <CloseIcon />
            </IconButton>
          )}

          {children}

          {/* 3-dot kebab menu — only when modal is forced (no close button) */}
          {!canClose && (
            <IconButton
              onClick={handleKebabOpen}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          <Menu
            anchorEl={kebabAnchor}
            open={Boolean(kebabAnchor)}
            onClose={handleKebabClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 0.5, zIndex: 9999 }}
          >
            {/* Switch Workspace */}
            <MenuItem onClick={handleWorkspaceMenuOpen}>
              <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
              Switch Workspace
            </MenuItem>

            {/* Nested workspace list */}
            <Menu
              anchorEl={workspaceMenuAnchor}
              open={Boolean(workspaceMenuAnchor)}
              onClose={handleWorkspaceMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ zIndex: 10000 }}
            >
              {loadingWorkspaces ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              ) : workspaces.length > 0 ? (
                workspaces.map((ws) => (
                  <MenuItem
                    key={ws.workspace_id?._id || ws._id}
                    onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
                    selected={ws.is_active}
                  >
                    {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No other workspaces</MenuItem>
              )}
            </Menu>

            {/* Log Out */}
            <MenuItem onClick={() => { handleKebabClose(); handleLogOut(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Log Out
            </MenuItem>
          </Menu>

          {/* Centred title */}
          <MDBox display="flex" justifyContent="center" alignItems="center" mb={3} mt={canClose ? { xs: 4, sm: 0 } : 0}>
            <Typography variant="h5" fontWeight="bold" textAlign="center">
              {showInstagramInfo ? "Connect Instagram" : "Connect Your Profile"}
            </Typography>
          </MDBox>

          {errorMessage && (
            <Box mb={2} px={1}>
              <Typography variant="body2" color="error" textAlign="center">
                {errorMessage}
              </Typography>
            </Box>
          )}

          {showInstagramInfo ? (
            <InstagramInstructions
              onBack={() => setShowInstagramInfo(false)}
              onProceed={() => handleConnect("instagram")}
            />
          ) : loading ? (
            /* [NEW] Detailed Progress UI */
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <MDBox position="relative" display="inline-flex">
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={40}
                  thickness={4}
                  sx={{ color: "#d1f2f2", position: "absolute" }}
                />
                <CircularProgress
                  variant="determinate"
                  value={progress}
                  size={40}
                  thickness={4}
                  color="info"
                />
                <MDBox
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MDTypography variant="caption" component="div" color="text" fontWeight="bold">
                    {`${Math.round(progress)}%`}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox mt={2}>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  {statusMessage || "Connecting..."}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox
              sx={{
                borderRadius: "16px",
                backgroundColor: "#f0f2f5",
                padding: "16px",
              }}
            >
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                {socialPlatforms.map((platform) => {
                  const isSelected = selectedPlatform === platform.handler;

                  return (
                    <Grid item key={platform.name}>
                      <Card
                        onClick={() => handleConnectClick(platform.handler)}
                        sx={{
                          cursor: "pointer",
                          width: 140,
                          height: 120,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "12px",
                          textAlign: "center",
                          border: isSelected
                            ? "2px solid #00A1A1"
                            : "1px solid #ddd",
                          boxShadow: isSelected ? 6 : 1,
                          backgroundColor: isSelected ? "grey.400" : "grey.100",
                          color: isSelected ? "black" : "text.primary",
                          position: "relative",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={platform.icon}
                          alt={platform.name}
                          sx={{ width: 48, height: 48, mb: 1 }}
                        />
                        <Typography
                          variant="button"
                          fontWeight={isSelected ? "bold" : "medium"}
                          sx={{ fontSize: "0.9rem" }}
                        >
                          {platform.name}
                        </Typography>

                        {isSelected && (
                          <MDBox
                            sx={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                              backgroundColor: "#26282b",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CheckCircleIcon color="accent" sx={{ fontSize: 18 }} />
                          </MDBox>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </MDBox>
          )}
        </Card>
      </Slide>
    </Backdrop>
  );
}

export default ConnectOwnProfile;
